import { ref, onMounted, onUnmounted, computed } from "vue";

// 定义与Tailwind断点匹配的尺寸
export type BreakpointSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

// 与Tailwind默认断点值匹配的像素值
const breakpoints: Record<BreakpointSize, number> = {
  xs: 390,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

export function useBreakpoint() {
  const currentBreakpoint = ref<BreakpointSize | null>(null);
  const width = ref(window.innerWidth);
  const height = ref(window.innerHeight);

  // 更新当前断点
  const updateBreakpoint = () => {
    width.value = window.innerWidth;
    height.value = window.innerHeight;

    if (width.value < breakpoints.xs) {
      currentBreakpoint.value = null;
    } else if (width.value < breakpoints.sm) {
      currentBreakpoint.value = "xs";
    } else if (width.value < breakpoints.md) {
      currentBreakpoint.value = "sm";
    } else if (width.value < breakpoints.lg) {
      currentBreakpoint.value = "md";
    } else if (width.value < breakpoints.xl) {
      currentBreakpoint.value = "lg";
    } else if (width.value < breakpoints["2xl"]) {
      currentBreakpoint.value = "xl";
    } else {
      currentBreakpoint.value = "2xl";
    }
  };

  // 检查当前断点是否大于或等于指定断点
  const isBreakpointOrLarger = (size: BreakpointSize): boolean => {
    if (!currentBreakpoint.value) return false;

    const breakpointOrder: BreakpointSize[] = [
      "xs",
      "sm",
      "md",
      "lg",
      "xl",
      "2xl",
    ];
    const currentIndex = breakpointOrder.indexOf(currentBreakpoint.value);
    const targetIndex = breakpointOrder.indexOf(size);

    return currentIndex >= targetIndex;
  };

  // 检查当前断点是否小于或等于指定断点
  const isBreakpointOrSmaller = (size: BreakpointSize): boolean => {
    if (!currentBreakpoint.value && size === "xs") return true;
    if (!currentBreakpoint.value) return false;

    const breakpointOrder: BreakpointSize[] = [
      "xs",
      "sm",
      "md",
      "lg",
      "xl",
      "2xl",
    ];
    const currentIndex = breakpointOrder.indexOf(currentBreakpoint.value);
    const targetIndex = breakpointOrder.indexOf(size);

    return currentIndex <= targetIndex;
  };

  // 检查当前断点是否匹配指定断点
  const isBreakpoint = (size: BreakpointSize): boolean => {
    return currentBreakpoint.value === size;
  };

  // 根据断点执行不同的逻辑
  const breakpointSwitch = <T>(
    cases: Partial<Record<BreakpointSize | "default", () => T>>
  ): T => {
    if (currentBreakpoint.value && cases[currentBreakpoint.value]) {
      return cases[currentBreakpoint.value]!();
    }

    // 如果没有匹配当前断点的处理函数，使用默认处理函数
    if (cases.default) {
      return cases.default();
    }

    throw new Error(
      "No matching breakpoint case found and no default provided"
    );
  };

  // 计算属性：是否为移动设备
  const isMobile = computed(
    () => !currentBreakpoint.value || currentBreakpoint.value === "sm"
  );

  // 计算属性：是否为平板设备
  const isTablet = computed(
    () => currentBreakpoint.value === "md" || currentBreakpoint.value === "lg"
  );

  // 计算属性：是否为桌面设备
  const isDesktop = computed(
    () => currentBreakpoint.value === "xl" || currentBreakpoint.value === "2xl"
  );

  // 计算属性：获取当前断点的CSS类名
  const breakpointClass = computed(() => currentBreakpoint.value || "xs");

  onMounted(() => {
    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", updateBreakpoint);
  });

  return {
    width,
    height,
    currentBreakpoint,
    isBreakpoint,
    isBreakpointOrLarger,
    isBreakpointOrSmaller,
    breakpointSwitch,
    isMobile,
    isTablet,
    isDesktop,
    breakpointClass,
  };
}
